import { ColumnDef, Row } from '@tanstack/react-table';
import { DataTableColumnMeta } from './types';
import { formatCellValue, getAlignment } from './utils';
import styles from './table-body.module.scss';

// ─────────────────────────────────────
// Props
// ─────────────────────────────────────

interface DefaultSubTableProps<TData> {
  row: Row<TData>;
  columns: ColumnDef<TData>[];
}

// ─────────────────────────────────────
// Component
// ─────────────────────────────────────

/**
 * Default expanded-row detail view.
 * Renders a vertical two-column table: column header → formatted value.
 * Used as the fallback when `config.expandableRows` is true but no
 * `renderRowSubComponent` is provided.
 * @return {JSX.Element} A vertical key-value table of row data
 */
export function DefaultSubTable<TData extends Record<string, unknown>>({
  row,
  columns,
}: DefaultSubTableProps<TData>) {
  return (
    <table className={styles.defaultSubTable}>
      <tbody>
        {columns.map((col) => {
          // Skip the expander column
          if (col.id === '_expander') return null;

          // Skip columns with no string header
          const header = col.header;
          if (typeof header !== 'string' || !header) return null;

          const columnId = col.id;
          if (!columnId) return null;

          const meta = col.meta as DataTableColumnMeta | undefined;
          const value = row.getValue(columnId);
          const alignment = getAlignment(meta);

          return (
            <tr key={columnId}>
              <th className={styles.defaultSubTableLabel}>{header}</th>
              <td
                className={styles.defaultSubTableValue}
                style={{ textAlign: alignment }}
              >
                {formatCellValue(value, meta)}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
